import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchRezeptInvoiceMassagers } from '@/services/main/rezept';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { useClinicContext } from './useClinicContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';

/**
 * Massager dropdown for the 領収書・請求書 screen, scoped to a single
 * treatement_yearmonth (YYYYMM). Refetches automatically when yearmonth
 * changes. Pass an empty string to disable until a value is known.
 */
export const useRezeptInvoiceMassagers = (
  treatementYearmonth: string,
  options?: { enabled?: boolean }
) => {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const enabled =
    (options?.enabled ?? true) && !!tenant && !!clinicNumber && /^\d{6}$/.test(treatementYearmonth);

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['rezept-invoice', 'massagers', tenant, clinicNumber, serviceType, treatementYearmonth],
    queryFn: () => fetchRezeptInvoiceMassagers(tenant, clinicNumber, treatementYearmonth),
    enabled,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isFetching, isError, error, refetch };
};

export default useRezeptInvoiceMassagers;
